import React from "react";
import { Group, ActionIcon, Tooltip } from "@mantine/core";
import { PermissionRequirement } from "constants/types/permission";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

export interface ActionItem<T> {
  key: string;
  label: string;
  icon: React.ReactNode;
  color?: string;
  onClick: (row: T) => void;
  hidden?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
  permission?: PermissionRequirement;
}

interface GroupActionsProps<T> {
  row: T;
  actions: ActionItem<T>[];
}

const GroupActions = <T extends Record<string, any>>({
  row,
  actions,
}: GroupActionsProps<T>) => {
  const ability = useAbility();

  return (
    <Group gap="xs" wrap="nowrap">
      {actions.map((action) => {
        // undefined permission → permissive (show), defined → check ability
        if (!canAccessRequirement(ability, action.permission, true)) return null;
        if (action.hidden && action.hidden(row)) return null;

        const isDisabled = action.disabled ? action.disabled(row) : false;

        return (
          <Tooltip
            key={action.key}
            label={action.label}
            withArrow
            position="top"
          >
            <ActionIcon
              variant="subtle"
              color={action.color || "gray"}
              onClick={() => action.onClick(row)}
              disabled={isDisabled}
              aria-label={action.label}
            >
              {action.icon}
            </ActionIcon>
          </Tooltip>
        );
      })}
    </Group>
  );
};

export default GroupActions;
